import {Observable} from 'rxjs';
import {throttleTime} from 'rxjs/operators';

import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {offlineConversationContents} from '../../../srcCommon/services/offline/offlineConversationContents';
import {
  inIdleOrHiddenCallbackAsync,
  reduceInIdleOrHiddenCallbackAsync,
} from '../../helpers/async/inIdleCallback';
import {InboxesFiltersEnum} from '../../models/url/inboxesUrlModel';
import {AppState} from '../../store/appState';
import {StateGetter} from '../../store/appStore';
import {getCellsLists} from '../../store/metadata/cellsLists/cellsListsSelectors';
import {Prefetcher} from './prefetchers/base';
import {ConversationContentPrefetcher} from './prefetchers/conversationContent';
import {computeMoreCellsEligibility} from './prefetchers/moreCells';

/*
 * Types.
 */

export interface PrefetcherReport
  extends PrefetcherBacklogReport,
    PrefetcherCellsCacheReport,
    PrefetcherConversationContentCacheReport {}

interface PrefetcherBacklogReport {
  backlogLengths: {[key: string]: number};
  totalBacklogLength: number;
  longestTaskTime: number;
}

interface PrefetcherCellsCacheReport {
  minCellsInterval: number;
}

interface PrefetcherConversationContentCacheReport {
  timelineCount: number;
  timelineInterval: number;
  timelinePrefetchedInterval: number;
}

/*
 * API.
 */

export function startPrefetcherReporter(
  getState: StateGetter,
  prefetchers: ReadonlyArray<Prefetcher<unknown>>,
  conversationContentPrefetcher: ConversationContentPrefetcher,
  cycles$: Observable<unknown>,
  interval: number,
  callback: (report: PrefetcherReport) => void,
) {
  const subscription = cycles$.pipe(throttleTime(interval, undefined)).subscribe(
    () => {
      const state = getState();

      createPrefetcherReportAsync(state, prefetchers, conversationContentPrefetcher).then(
        callback,
        (error) => {
          reportToBugsnag(error);
        },
      );
    },
    (error) => {
      reportToBugsnag(error);
    },
  );

  return () => subscription.unsubscribe();
}

/*
 * Helpers.
 */

async function createPrefetcherReportAsync(
  state: AppState,
  prefetchers: ReadonlyArray<Prefetcher<unknown>>,
  conversationContentPrefetcher: ConversationContentPrefetcher,
): Promise<PrefetcherReport> {
  const [backlogReport, cellsCacheReport, conversationContentCacheReport] = await Promise.all([
    createPrefetcherBacklogReportAsync(state, prefetchers),
    createPrefetcherCellsCacheReportAsync(state),
    createPrefetcherConversationContentCacheReportAsync(conversationContentPrefetcher),
  ]);

  return {...backlogReport, ...cellsCacheReport, ...conversationContentCacheReport};
}

async function createPrefetcherBacklogReportAsync(
  state: AppState,
  prefetchers: ReadonlyArray<Prefetcher<unknown>>,
): Promise<PrefetcherBacklogReport> {
  return reduceInIdleOrHiddenCallbackAsync(
    prefetchers,
    (previousReport: PrefetcherBacklogReport, prefetcher) => {
      const {key} = prefetcher;

      const startTime = performance.now();
      const backlogLength = prefetcher.computeBacklogLength(state);
      const taskTime = performance.now() - startTime;

      return {
        backlogLengths: {
          ...previousReport.backlogLengths,
          [key]: backlogLength,
        },
        totalBacklogLength: previousReport.totalBacklogLength + backlogLength,
        longestTaskTime: Math.max(previousReport.longestTaskTime, taskTime),
      };
    },
    {
      backlogLengths: {},
      totalBacklogLength: 0,
      longestTaskTime: 0,
    },
    {
      source: TaskSourcesEnum.PREFETCH,
    },
  );
}

async function createPrefetcherCellsCacheReportAsync(state: AppState): Promise<PrefetcherCellsCacheReport> {
  const now = Date.now();

  return reduceInIdleOrHiddenCallbackAsync(
    getCellsLists(state),
    (previousReport: PrefetcherCellsCacheReport, list) => {
      const eligibility = computeMoreCellsEligibility(list, InboxesFiltersEnum.OPEN);
      if (!eligibility) {
        return previousReport;
      }

      const interval = eligibility.bottomBoundary
        ? Math.max(0, now - eligibility.bottomBoundary.date)
        : Infinity;

      return {
        minCellsInterval: Math.min(previousReport.minCellsInterval, interval),
      };
    },
    {
      minCellsInterval: Infinity,
    },
    {
      source: TaskSourcesEnum.PREFETCH,
    },
  );
}

async function createPrefetcherConversationContentCacheReportAsync(
  prefetcher: ConversationContentPrefetcher,
): Promise<PrefetcherConversationContentCacheReport> {
  const {
    count,
    minTimestamp = Date.now(),
    maxTimestamp = 0,
  } = await inIdleOrHiddenCallbackAsync(() => offlineConversationContents.readStatsAsync(), {
    source: TaskSourcesEnum.PREFETCH,
  });

  return {
    timelineCount: count,
    timelineInterval: Math.max(0, maxTimestamp - minTimestamp),
    timelinePrefetchedInterval: prefetcher.prefetchedBumpInterval,
  };
}
